import { Injectable } from '@angular/core';
import {
	PluginType,
	MultiChoiceGameData,
	MultiChoiceQuestion,
	PluginInterfaceId,
} from '@meta-press/shared-plugins';
import {
	GenerationRequest,
	GenerationResult,
	GenerationPrompts,
	ValidationResult,
} from './base/game-generation.interface';
import { BaseGenerationService } from './base/base-generation.service';
import { SettingsService } from '../settings-service/settings.service';

/**
 * Multi-choice specific generation service
 */
@Injectable({
	providedIn: 'root',
})
export class MultiChoiceGenerationService extends BaseGenerationService<MultiChoiceGameData> {
	readonly pluginType = PluginType.MultiChoice;

	/**
	 * Generate dynamic JSON example answers based on answer count
	 * @param answerCount Number of answers to generate
	 * @returns JSON string with answer examples
	 */
	private generateAnswerExamples(answerCount: number): string {
		const answers = [];
		for (let i = 1; i <= answerCount; i++) {
			answers.push({
				text: `Kysymyksen vastausvaihtoehto ${i}`,
				isCorrect: i === 2, // Make the second answer correct by default, 1st is the actual answer given by user
			});
		}
		return JSON.stringify(answers, null, 16).replace(
			/^/gm,
			'                ',
		); // Proper indentation
	}

	/**
	 * Create system and user prompts for multi-choice question generation
	 * @param request The generation request
	 * @param settingsService Settings service for language preferences
	 * @returns System and user prompts
	 */
	createPrompts(
		request: GenerationRequest,
		settingsService: SettingsService,
	): GenerationPrompts {
		const { parameters, sourceMaterial } = request;

		let systemPrompt: string;
		let userPrompt: string;

		// Get target language prompt from settings service
		const targetLanguagePrompt = settingsService.GetTargetLanguagePrompt();

		// Get target difficulty prompt from settings service
		const targetDifficultyPrompt =
			settingsService.GetTargetDifficultyPrompt();

		const answerCount = parameters['answerCount'] || 4;

		if (parameters['questionTopic']?.trim()) {
			// Topic-based generation
			systemPrompt = `Olet monivalintakysymysten laatija. Tehtäväsi on luoda laadukkaita monivalintakysymyksiä annetusta aiheesta. 
Luo kiinnostavia kysymyksiä ja vastausvaihtoehtoja aiheesta.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

			userPrompt = `Luo ${parameters['numberOfQuestions'] || 5} monivalintakysymystä aiheesta: "${parameters['questionTopic']}"

Noudata seuraavia ohjeita:
- Luo pelin otsikko, joka kuvaa aihetta (esim. "Suomen historia", "Matematiikan perusteet", jne.)
- Luo jokaiselle kysymykselle ${answerCount} vastausvaihtoehtoa. Annan alla placeholderit vastausvaihtoehdoilla - korvaa ne aiheeseen liittyvillä vaihtoehdoilla
- Merkitse oikea vastaus
- Varmista, että kysymykset ovat selkeitä ja yksiselitteisiä
- Luo jokaiselle kysymykselle selitys väärille vastauksille (explanationText, kertoo aina ensin oikean vastauksen ja sitten sen perustelun) ja oikealle vastaukselle (correctExplanationText, vahvistaa vastauksen oikeaksi ja kertoo lisää triviaa, mutta lyhyesti.)

Vastaa JSON-muodossa:
{
    "title": "Pelin otsikko, joka kuvaa aihetta",
    "questions": [
        {
            "questionText": "Kysymyksen teksti",
            "answers": ${this.generateAnswerExamples(answerCount)}, // Korvaa nämä aiheeseen liittyvillä vastausvaihtoehdoilla
            "explanationText": "Kerro aina ensin, mikä olisi ollut oikea vastaus. Selitä, miksi se on oikea vastaus.",
            "correctExplanationText": "Selitys, joka vahvistaa oikean vastauksen. Kerro tässä myös ihan vähän lisätietoa oikeasta vastauksesta. Ole kuitenkin ytimekäs.",
			"imageId": -1 // aina -1
        }
    ]
}`;
		} else {
			// Source material based generation
			systemPrompt = `Olet monivalintakysymysten laatija. Tehtäväsi on luoda laadukkaita monivalintakysymyksiä annetusta lähdemateriaalista. 
Selvitä, mistä lähdemateriaalissa on kyse ja käytä lähdemateriaalissa olevia faktoja. Luo niistä kiinnostavia kysymyksiä ja vastausvaihtoehtoja.

${targetLanguagePrompt}
${targetDifficultyPrompt}`;

			let includeImages = '';

			switch (request.imagePolicy) {
				case 'smart':
					{
						includeImages = sourceMaterial?.imagesAsString
							? `\n\nLähdemateriaalissa on kuvia. Käytä kuvia aina kun mahdollista ja ne sopivat kysymykseen. Älä kuitenkaan käytä kuvaa, jos se ei liity kysymyksen aiheeseen. Tässä kuvamateriaali: ${sourceMaterial.imagesAsString}`
							: '';
					}
					break;

				case 'force':
					{
						includeImages = sourceMaterial?.imagesAsString
							? `\n\nLähdemateriaalissa on kuvia. Lisää jokaisen kysymyksen yhteyteen kuva. Et saa jättää yhtäkään kysymystä ilman kuvaa. Yritä löytää kuvan metadatan perusteella parhaiten kysymykseen sopiva kuva. Tässä kuvamateriaali: ${sourceMaterial.imagesAsString}`
							: '';
					}
					break;

				case 'never':
					{
						// No need to instruct about images
						includeImages = sourceMaterial?.imagesAsString
							? `\n\nLähdemateriaalissa on kuvien metatietoa. Käytä metatietoa kysymyksiä luodessa, mutta älä liitä kuvien id:itä kysymysten yhteyteen. Kysymyksen kuvien id tulee olla aina -1. Tässä kuvamateriaalin metadata: ${sourceMaterial.imagesAsString}`
							: '';
					}
					break;
			}

			userPrompt = `Luo ${parameters['numberOfQuestions'] || 5} monivalintakysymystä valitusta lähdemateriaalista. ${includeImages}

Noudata seuraavia ohjeita:
- Luo pelin otsikko, joka kuvaa lähdemateriaalin aihetta (esim. "Suomen historia", "Kemian perusteet", jne.)
- Luo jokaiselle kysymykselle ${answerCount} vastausvaihtoehtoa. Annan alla placeholderit vastausvaihtoehdoille - korvaa ne lähdemateriaalin pohjalta luoduilla vastausvaihtoehdoilla
- Merkitse oikea vastaus
- Perusta kysymykset lähdemateriaalin faktaisiin tietoihin
- Varmista, että väärät vastausvaihtoehdot ovat uskottavia mutta selvästi vääriä
- Luo jokaiselle kysymykselle selitys väärille vastauksille (explanationText, kertoo aina ensin oikean vastauksen ja sitten sen perustelun) ja oikealle vastaukselle (correctExplanationText, vahvistaa vastauksen oikeaksi ja kertoo lisää triviaa, mutta lyhyesti.)

Vastaa JSON-muodossa:
{
    "title": "Pelin otsikko, joka kuvaa lähdemateriaalin aihetta",
    "questions": [
        {
            "questionText": "Kysymyksen teksti",
            "answers": ${this.generateAnswerExamples(answerCount)}, // Korvaa nämä lähdemateriaalin pohjalta luoduilla vastausvaihtoehdoilla
            "explanationText": "Kerro aina ensin, mikä olisi ollut oikea vastaus. Selitä, miksi se on oikea vastaus.",
            "correctExplanationText": "Selitys, joka vahvistaa oikean vastauksen. Kerro tässä myös ihan vähän lisätietoa oikeasta vastauksesta. Ole kuitenkin ytimekäs.",
			"imageId": -1 // käytettävän kuvan articleId. Jos olet valinnut kysymyksen yhteyteen kuvan, laita sen articleId tähän. articleId:t löytyvät saamasi kuvalistan id-kentistä. Laita muutoin -1.
        }
    ]
}

${targetLanguagePrompt}
${targetDifficultyPrompt}
`;
		}

		return { system: systemPrompt, user: userPrompt };
	}

	/**
	 * Process AI response into MultiChoiceGameData
	 * @param response The AI service response
	 * @param parameters Original generation parameters
	 * @returns Generation result with game data or error
	 */
	protected processResponseCore(
		response: any,
		parameters: any,
	): GenerationResult {
		try {
			const generatedQuestions = response.json?.questions;
			const generatedTitle = response.json?.title;

			if (
				!generatedQuestions ||
				!Array.isArray(generatedQuestions) ||
				generatedQuestions.length === 0
			) {
				return {
					success: false,
					error: 'Invalid response format: no questions found',
					rawResponse: response.rawResponse,
				};
			}

			// Validate question structure
			for (const question of generatedQuestions) {
				if (
					!question.questionText ||
					!question.answers ||
					!Array.isArray(question.answers)
				) {
					return {
						success: false,
						error: 'Invalid question structure in response',
						rawResponse: response.rawResponse,
					};
				}

				// Ensure at least one correct answer
				const hasCorrectAnswer = question.answers.some(
					(answer: any) => answer.isCorrect === true,
				);
				if (!hasCorrectAnswer) {
					return {
						success: false,
						error: 'Question without correct answer found',
						rawResponse: response.rawResponse,
					};
				}
			}

			// Create game data
			const gameData = new MultiChoiceGameData();
			gameData.title = generatedTitle || ''; // Set title from AI response or empty string
			gameData.questions = generatedQuestions as MultiChoiceQuestion[];
			gameData.displayMode = parameters.mode || 'sequential';

			gameData.selectedInterfaceId = parameters.selectedInterfaceId;
			// Set selectedInterface based on interfaceType parameter for backward compatibility
			// if (parameters.interfaceType === 'Yellow') {
			// 	gameData.selectedInterfaceId = 0; // Yellow
			// } else if (parameters.interfaceType === 'Neutral') {
			// 	gameData.selectedInterfaceId = 1; // Neutral
			// } else if (parameters.interfaceType === 'MP-Blue') {
			// 	gameData.selectedInterfaceId = 2; // Blue
			// } else {
			// 	gameData.selectedInterfaceId = 0; // Default to Yellow
			// }

			return {
				success: true,
				gameData,
				rawResponse: response.rawResponse,
			};
		} catch (error) {
			return {
				success: false,
				error: `Response processing failed: ${error}`,
				rawResponse: response.rawResponse,
			};
		}
	}

	/**
	 * Validate multi-choice specific request
	 */
	validateRequest(request: GenerationRequest): ValidationResult {
		const hasSourceMaterial =
			request.sourceMaterial &&
			request.sourceMaterial.fileHandles.length > 0;
		const hasTopic =
			request.parameters['questionTopic'] &&
			request.parameters['questionTopic'].trim();

		if (!hasSourceMaterial && !hasTopic) {
			return {
				valid: false,
				error: hasSourceMaterial
					? 'Valitse vähintään yksi artikkeli lähteeksi'
					: 'Syötä aihe kysymyksille tai valitse lähdemateriaali',
			};
		}

		return { valid: true };
	}

	/**
	 * Get default parameters for multi-choice
	 */
	getDefaultParameters(): any {
		return {
			numberOfQuestions: 5,
			clearPrevious: false,
			answerCount: 3,
			mode: 'sequential',
			selectedInterfaceId: PluginInterfaceId.Interface2,
		};
	}

	/**
	 * Merge new questions with existing game data based on parameters
	 * @param existingGameData Current game data
	 * @param newQuestions Generated questions
	 * @param clearPrevious Whether to clear existing questions
	 * @returns Updated game data
	 */
	override mergeContent(
		existingGameData: MultiChoiceGameData,
		newGameData: MultiChoiceGameData,
		clearPrevious: boolean,
	): MultiChoiceGameData {
		const newQuestions = newGameData.questions || [];

		// Merge title from AI-generated data if it exists and current title is empty
		if (newGameData.title && !existingGameData.title) {
			existingGameData.title = newGameData.title;
		}

		if (clearPrevious) {
			existingGameData.questions = newQuestions;
		} else {
			// If first question is empty placeholder, remove it
			if (
				existingGameData.questions.length === 1 &&
				(!existingGameData.questions[0].questionText ||
					existingGameData.questions[0].questionText ===
						'Uusi kysymys')
			) {
				existingGameData.questions = [];
			}

			// Add new questions to existing ones
			existingGameData.questions.push(...newQuestions);
		}

		return existingGameData;
	}
}
